import { useState, useEffect, useCallback, useMemo } from 'react';

import {
    getBoardConfigs,
    createBoardConfig,
    updateBoardConfig,
    deleteBoardConfig,
    type BoardConfigDto,
    type BoardConfigPayload,
} from '@/api/client';

interface Props {
    getToken: () => Promise<string>;
    facilities: { FacilityCode: string; DisplayName: string }[];
}

/**
 * DOCKVIEW SCREENS admin (dock.BoardConfig, migration 136) — the last piece of
 * backlog #8.
 *
 * Every setting here used to be a URL query param, which meant a wall display
 * was "configured" by whoever last typed its address. Now a screen is a saved
 * row and the TV's URL only has to name it: /board?cfg=<key>.
 *
 * The URL is the point of this screen, so it's shown per row with a copy button
 * — the job it exists for is "set up a new TV", and that job ends with pasting
 * an address into a browser on the dock floor.
 *
 * A facility with NO config still works: the board falls back to the values it
 * has always shipped. Nothing here is required to make a board run.
 */

const TEXT_SCALES: { value: BoardConfigDto['textScale']; label: string }[] = [
    { value: 'auto',   label: 'Automatic (recommended)' },
    { value: 'base',   label: 'Small — more doors on screen' },
    { value: 'roomy',  label: 'Large' },
    { value: 'sparse', label: 'Largest — for a distant TV' },
];

const BLANK: BoardConfigPayload = {
    facilityCode: '',
    configKey: '',
    displayName: '',
    isDefault: false,
    isActive: true,
    doorsPerPage: 12,
    rotateSeconds: 10,
    refreshSeconds: 15,
    showHolding: true,
    showOpenDoors: true,
    showReservedCards: true,
    textScale: 'auto',
    standDownMinutes: 60,
    doorGroup: null,
};

function boardUrl(configKey: string): string {
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    return `${origin}/board?cfg=${encodeURIComponent(configKey)}`;
}

export default function BoardConfigAdmin({ getToken, facilities }: Props) {
    const [rows, setRows] = useState<BoardConfigDto[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    /** null = not editing; number = editing that id; 'new' = creating. */
    const [editing, setEditing] = useState<number | 'new' | null>(null);
    const [draft, setDraft] = useState<BoardConfigPayload>(BLANK);
    const [copied, setCopied] = useState<string | null>(null);

    const facilityName = useMemo(() => {
        const m = new Map(facilities.map((f) => [f.FacilityCode, f.DisplayName]));
        return (code: string | null) => (code ? m.get(code) ?? code : '—');
    }, [facilities]);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const { configs } = await getBoardConfigs(await getToken());
            setRows(configs);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Could not load the dockview screens.');
        } finally {
            setLoading(false);
        }
    }, [getToken]);

    useEffect(() => { void load(); }, [load]);

    function startNew() {
        setDraft({ ...BLANK, facilityCode: facilities[0]?.FacilityCode ?? '' });
        setEditing('new');
        setNotice(null);
        setError(null);
    }

    function startEdit(c: BoardConfigDto) {
        setDraft({
            facilityCode: c.facilityCode ?? '',
            configKey: c.configKey ?? '',
            displayName: c.displayName,
            isDefault: c.isDefault,
            isActive: c.isActive,
            doorsPerPage: c.doorsPerPage,
            rotateSeconds: c.rotateSeconds,
            refreshSeconds: c.refreshSeconds,
            showHolding: c.showHolding,
            showOpenDoors: c.showOpenDoors,
            showReservedCards: c.showReservedCards,
            textScale: c.textScale,
            // Configs written before migration 137 have no value of their own;
            // the API defaults them to 60 on read, so this is only a guard
            // against an older payload reaching a newer screen.
            standDownMinutes: c.standDownMinutes ?? 60,
            doorGroup: c.doorGroup,
        });
        setEditing(c.boardConfigId);
        setNotice(null);
        setError(null);
    }

    async function save() {
        setBusy(true);
        setError(null);
        setNotice(null);
        try {
            const token = await getToken();
            const body: BoardConfigPayload = {
                ...draft,
                configKey: draft.configKey.trim().toLowerCase(),
                doorGroup: draft.doorGroup?.trim() ? draft.doorGroup.trim() : null,
            };
            const { configs } = editing === 'new'
                ? await createBoardConfig(token, body)
                : await updateBoardConfig(token, editing as number, body);
            setRows(configs);
            setEditing(null);
            setNotice(`Saved “${body.displayName}”. A screen picks it up on its next refresh.`);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Save failed.');
        } finally {
            setBusy(false);
        }
    }

    async function remove(c: BoardConfigDto) {
        // No confirm dialog available in this panel's idiom, so make the action
        // itself two-step: the button asks, the second click does it.
        if (deleteArmed !== c.boardConfigId) { setDeleteArmed(c.boardConfigId); return; }
        setBusy(true);
        setError(null);
        try {
            const { configs } = await deleteBoardConfig(await getToken(), c.boardConfigId as number);
            setRows(configs);
            setNotice(`Deleted “${c.displayName}”. Any TV still pointed at it falls back to the site default.`);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Delete failed.');
        } finally {
            setBusy(false);
            setDeleteArmed(null);
        }
    }
    const [deleteArmed, setDeleteArmed] = useState<number | null>(null);

    async function copyUrl(key: string) {
        const url = boardUrl(key);
        try {
            await navigator.clipboard.writeText(url);
            setCopied(key);
            window.setTimeout(() => setCopied(null), 2000);
        } catch {
            setError(`Could not copy. The URL is ${url}`);
        }
    }

    const grouped = useMemo(() => {
        const m = new Map<string, BoardConfigDto[]>();
        for (const r of rows) {
            const k = r.facilityCode ?? '—';
            m.set(k, [...(m.get(k) ?? []), r]);
        }
        return [...m.entries()].sort((a, b) => facilityName(a[0]).localeCompare(facilityName(b[0])));
    }, [rows, facilityName]);

    if (loading) return <div className="text-sm text-muted-foreground">Loading dockview screens…</div>;

    const num = (v: string, min: number, max: number, fallback: number) => {
        const n = Number.parseInt(v, 10);
        if (!Number.isFinite(n)) return fallback;
        return Math.min(max, Math.max(min, n));
    };

    return (
        <div className="space-y-4">
            <div>
                <h3 className="text-base font-medium">Dockview screens</h3>
                <p className="text-sm text-muted-foreground mt-1">
                    One row per wall display. The TV&apos;s address names the screen — copy the URL from the
                    row and open it on that TV. A site&apos;s <strong>default</strong> screen is what a plain{' '}
                    <code>/board</code> shows, so the main dock TV needs no query string at all. Changes take
                    effect on the screen&apos;s next refresh; nothing needs a deploy.
                </p>
            </div>

            {error && (
                <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                    {error}
                </div>
            )}
            {notice && !error && (
                <div className="rounded-md border border-emerald-600/40 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
                    {notice}
                </div>
            )}

            {editing === null && (
                <button
                    type="button"
                    onClick={startNew}
                    disabled={facilities.length === 0}
                    className="rounded-md border px-3 py-1.5 text-sm font-medium disabled:opacity-40"
                >
                    Add a screen
                </button>
            )}

            {editing !== null && (
                <div className="rounded-lg border p-4 space-y-4">
                    <div className="text-sm font-medium">
                        {editing === 'new' ? 'New dockview screen' : `Editing “${draft.displayName}”`}
                    </div>

                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <label className="text-sm">
                            <span className="block text-xs text-muted-foreground mb-1">Facility</span>
                            <select
                                value={draft.facilityCode}
                                onChange={(e) => setDraft({ ...draft, facilityCode: e.target.value })}
                                disabled={editing !== 'new'}
                                className="w-full rounded-md border px-2 py-1.5 text-sm disabled:opacity-60"
                            >
                                {facilities.map((f) => (
                                    <option key={f.FacilityCode} value={f.FacilityCode}>
                                        {f.DisplayName} ({f.FacilityCode})
                                    </option>
                                ))}
                            </select>
                        </label>

                        <label className="text-sm">
                            <span className="block text-xs text-muted-foreground mb-1">
                                Screen name — what the TV is called
                            </span>
                            <input
                                value={draft.displayName}
                                onChange={(e) => setDraft({ ...draft, displayName: e.target.value })}
                                placeholder="Brighton main dock TV"
                                className="w-full rounded-md border px-2 py-1.5 text-sm"
                            />
                        </label>

                        <label className="text-sm">
                            <span className="block text-xs text-muted-foreground mb-1">
                                URL key — the <code>?cfg=</code> value
                            </span>
                            <input
                                value={draft.configKey}
                                onChange={(e) => setDraft({ ...draft, configKey: e.target.value })}
                                placeholder="main"
                                className="w-full rounded-md border px-2 py-1.5 text-sm font-mono"
                            />
                        </label>

                        <label className="text-sm">
                            <span className="block text-xs text-muted-foreground mb-1">
                                Warehouse side — blank shows every door
                            </span>
                            <input
                                value={draft.doorGroup ?? ''}
                                onChange={(e) => setDraft({ ...draft, doorGroup: e.target.value })}
                                placeholder="EAST"
                                className="w-full rounded-md border px-2 py-1.5 text-sm"
                            />
                        </label>

                        <label className="text-sm">
                            <span className="block text-xs text-muted-foreground mb-1">Doors per page</span>
                            <input
                                type="number" min={1} max={200} value={draft.doorsPerPage}
                                onChange={(e) => setDraft({ ...draft, doorsPerPage: num(e.target.value, 1, 200, 12) })}
                                className="w-full rounded-md border px-2 py-1.5 text-sm"
                            />
                        </label>

                        <label className="text-sm">
                            <span className="block text-xs text-muted-foreground mb-1">
                                Seconds per page — only matters when doors overflow one page
                            </span>
                            <input
                                type="number" min={3} max={600} value={draft.rotateSeconds}
                                onChange={(e) => setDraft({ ...draft, rotateSeconds: num(e.target.value, 3, 600, 10) })}
                                className="w-full rounded-md border px-2 py-1.5 text-sm"
                            />
                        </label>

                        <label className="text-sm">
                            <span className="block text-xs text-muted-foreground mb-1">
                                Refresh seconds — how fast the floor sees a change
                            </span>
                            <input
                                type="number" min={5} max={600} value={draft.refreshSeconds}
                                onChange={(e) => setDraft({ ...draft, refreshSeconds: num(e.target.value, 5, 600, 15) })}
                                className="w-full rounded-md border px-2 py-1.5 text-sm"
                            />
                        </label>

                        <label className="text-sm">
                            <span className="block text-xs text-muted-foreground mb-1">Text size</span>
                            <select
                                value={draft.textScale}
                                onChange={(e) => setDraft({ ...draft, textScale: e.target.value as BoardConfigDto['textScale'] })}
                                className="w-full rounded-md border px-2 py-1.5 text-sm"
                            >
                                {TEXT_SCALES.map((s) => (
                                    <option key={s.value} value={s.value}>{s.label}</option>
                                ))}
                            </select>
                        </label>

                        <label className="text-sm">
                            <span className="block text-xs text-muted-foreground mb-1">
                                Stand-down minutes — how long a no-show or cancellation stays up
                            </span>
                            <input
                                type="number" min={0} max={480} value={draft.standDownMinutes}
                                onChange={(e) => setDraft({ ...draft, standDownMinutes: num(e.target.value, 0, 480, 60) })}
                                className="w-full rounded-md border px-2 py-1.5 text-sm"
                            />
                            <span className="block text-xs text-muted-foreground mt-1">
                                0 turns it off. A dead card never takes a door tile from a truck
                                that is actually there.
                            </span>
                        </label>
                    </div>

                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                        {([
                            ['isDefault', 'This site’s default screen (a plain /board shows this one)'],
                            ['isActive', 'Active'],
                            ['showHolding', 'Show the “waiting for door” strip'],
                            ['showOpenDoors', 'Show the “open doors” strip'],
                            ['showReservedCards', 'Give a dedicated empty door its own card'],
                        ] as const).map(([field, label]) => (
                            <label key={field} className="flex items-start gap-2 text-sm">
                                <input
                                    type="checkbox"
                                    checked={draft[field]}
                                    onChange={(e) => setDraft({ ...draft, [field]: e.target.checked })}
                                    className="mt-0.5"
                                />
                                <span>{label}</span>
                            </label>
                        ))}
                    </div>

                    {draft.configKey.trim() && (
                        <p className="text-xs text-muted-foreground">
                            This screen will be at{' '}
                            <code className="font-mono">{boardUrl(draft.configKey.trim().toLowerCase())}</code>
                        </p>
                    )}

                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={() => void save()}
                            disabled={busy || !draft.facilityCode || !draft.configKey.trim() || !draft.displayName.trim()}
                            className="rounded-md border px-3 py-1.5 text-sm font-medium disabled:opacity-40"
                        >
                            {busy ? 'Saving…' : 'Save'}
                        </button>
                        <button
                            type="button"
                            onClick={() => { setEditing(null); setError(null); }}
                            disabled={busy}
                            className="rounded-md px-3 py-1.5 text-sm text-muted-foreground"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}

            {rows.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                    No screens configured yet. Every board is running on the shipped defaults — 12 doors a page,
                    rotating every 10 seconds, refreshing every 15 — which is a perfectly good place to stay
                    until a site needs something different.
                </p>
            ) : (
                <div className="space-y-5">
                    {grouped.map(([code, configs]) => (
                        <div key={code}>
                            <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-1">
                                {facilityName(code)}
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                                            <th className="py-2 pr-4 font-medium">Screen</th>
                                            <th className="py-2 pr-4 font-medium">Shows</th>
                                            <th className="py-2 pr-4 font-medium">Timing</th>
                                            <th className="py-2 pr-4 font-medium">URL</th>
                                            <th className="py-2 pr-4 font-medium" />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {configs.map((c) => (
                                            <tr key={c.boardConfigId} className="border-b last:border-0 align-top">
                                                <td className="py-2 pr-4">
                                                    <div className="font-medium">
                                                        {c.displayName}
                                                        {c.isDefault && (
                                                            <span className="ml-2 rounded bg-blue-100 px-1.5 py-0.5 text-[10px] font-medium text-blue-800">
                                                                DEFAULT
                                                            </span>
                                                        )}
                                                        {!c.isActive && (
                                                            <span className="ml-2 rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
                                                                off
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="text-xs text-muted-foreground font-mono">{c.configKey}</div>
                                                </td>
                                                <td className="py-2 pr-4 text-xs text-muted-foreground">
                                                    {c.doorGroup ?? 'all doors'} · {c.doorsPerPage}/page
                                                    <br />
                                                    {[
                                                        c.showHolding && 'waiting strip',
                                                        c.showOpenDoors && 'open doors',
                                                        c.showReservedCards && 'reserved cards',
                                                    ].filter(Boolean).join(' · ') || 'doors only'}
                                                    {c.textScale !== 'auto' && <><br />text: {c.textScale}</>}
                                                    <br />stand-down: {c.standDownMinutes > 0 ? `${c.standDownMinutes} min` : 'off'}
                                                </td>
                                                <td className="py-2 pr-4 text-xs text-muted-foreground">
                                                    rotate {c.rotateSeconds}s
                                                    <br />
                                                    refresh {c.refreshSeconds}s
                                                </td>
                                                <td className="py-2 pr-4">
                                                    <button
                                                        type="button"
                                                        onClick={() => void copyUrl(c.configKey ?? '')}
                                                        className="text-xs underline decoration-dotted"
                                                        title={boardUrl(c.configKey ?? '')}
                                                    >
                                                        {copied === c.configKey ? 'Copied!' : 'Copy URL'}
                                                    </button>
                                                </td>
                                                <td className="py-2 pr-4 text-right whitespace-nowrap">
                                                    <button
                                                        type="button"
                                                        onClick={() => startEdit(c)}
                                                        disabled={busy}
                                                        className="rounded-md border px-2 py-1 text-xs font-medium disabled:opacity-40"
                                                    >
                                                        Edit
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => void remove(c)}
                                                        disabled={busy}
                                                        className="ml-2 rounded-md border px-2 py-1 text-xs font-medium text-destructive disabled:opacity-40"
                                                    >
                                                        {deleteArmed === c.boardConfigId ? 'Really delete?' : 'Delete'}
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <p className="text-xs text-muted-foreground">
                A TV pointed at a screen that has been deleted or switched off does not go blank — it falls
                back to this site&apos;s default screen, and to the shipped defaults if there isn&apos;t one.
                Query params on the URL (<code>?doors=</code>, <code>?open=0</code>, <code>?scale=roomy</code> …)
                still override a saved screen, which is how you try a setting on a laptop before committing it
                to a display the whole floor can see.
            </p>
        </div>
    );
}
